/*------------------------------------*\
    MODULES
\*------------------------------------*/
import loadGoogleMapsApi from "load-google-maps-api";
import 'whatwg-fetch';

/*------------------------------------*\
    MAP
\*------------------------------------*/
class Map {
    constructor(passedElement) {
        let self = this;

        self.elem = passedElement;
        self.apiKey = self.elem.getAttribute('data-key');
        self.language = self.elem.getAttribute('data-language');
        self.addressForm = self.elem.querySelector('[data-map-address-form]');
        self.addressFormInput = self.elem.querySelector('[data-map-address-form-input]');
        self.sections = self.elem.querySelector('[data-map-sections]');
        self.sectionsItemsContainer = self.elem.querySelector('[data-map-section-items]');
        self.sectionsItems = [];
        self.selectedSectionsItemIndex = null;
        self.mapElem = self.elem.querySelector('[data-map]');
        self.sectionBreakdown = self.elem.querySelector('[data-section-breakdown]');
        self.sectionBreakdownButton = self.elem.querySelector('[data-section-breakdown-button]');
        self.sectionBreakdownItems = self.elem.querySelector('[data-section-breakdown-items]');
        self.sectionBreakdownIsOpen = false;
        self.setMarkers = [];
    }

    /*-------------------------------------------------*\
        Init
    \*-------------------------------------------------*/
    init() {
        let self = this;

        // Load the Google Maps API, passing in our API key
        loadGoogleMapsApi({ key: self.apiKey }).then(function (googleMaps) {

            // Call the function that initialises the rendering of the map
            self.googleMaps = googleMaps;

            self.addressForm.addEventListener('submit', self.getAddressFormSubmission.bind(self));

            // Fetch the map data
            self.fetchMapData();

        }).catch(function (error) {
            console.error(error)
        });

        // Toggle section breakdown dropdown
        self.sectionBreakdownButton.addEventListener('click', evt => {
            if (self.sectionBreakdownIsOpen) {
                self.closeSectionBreakdown();
            } else {
                self.openSectionBreakdown();
            }
        });

        // Open section breakdown on page load?
        if(self.sectionBreakdown.hasAttribute('data-section-breakdown-open')) {
            self.openSectionBreakdown();
        }
    }

    /*-------------------------------------------------*\
        Open section breakdown
    \*-------------------------------------------------*/
    openSectionBreakdown() {
        let self = this;

        self.sectionBreakdown.classList.add('is-open');
        self.sectionBreakdownButton.setAttribute('aria-expanded', true);
        self.sectionBreakdownIsOpen = true;
    }

    /*-------------------------------------------------*\
        Close section breakdown
    \*-------------------------------------------------*/
    closeSectionBreakdown() {
        let self = this;

        self.sectionBreakdown.classList.remove('is-open');
        self.sectionBreakdownButton.setAttribute('aria-expanded', false);
        self.sectionBreakdownIsOpen = false;
    }

    /*-------------------------------------------------*\
        Fetch map data
    \*-------------------------------------------------*/
    fetchMapData() {
        let self = this;

        // Fetch the marker data
        fetch(`/data/map-data-${self.language}.json`)
            .then(function (response) {
                return response.json();
            }).then(function (json) {
                self.mapData = json;

                // Render the sections
                self.renderSections();

                // Render the map
                self.renderMap();

                // Listen for click events
                self.events();
            }).catch(function (ex) {
                console.log('Parsing failed', ex);
            });
    }

    /*-------------------------------------------------*\
        Render sections
    \*-------------------------------------------------*/
    renderSections() {
        let self = this;

        self.mapData.map(item => {
            // Create li element
            let li = document.createElement('li');
            li.setAttribute('class', 'sections__item');
            li.classList.add(item.backgroundColour);

            // Create a element
            let a = document.createElement('a');
            a.setAttribute('class', 'sections__item-link');
            a.setAttribute('data-map-sections-item', '');
            a.setAttribute('data-section', item.sectionHandle);
            a.setAttribute('href', '');
            a.textContent = item.sectionName;

            // Append a element to li element
            li.append(a);

            // Append li element to self.sectionsItemsContainer
            self.sectionsItemsContainer.append(li);

            // Add sections item to global self.sectionsItems var
            self.sectionsItems.push(a);
        });
    }

    /*-------------------------------------------------*\
        Render map
    \*-------------------------------------------------*/
    renderMap() {
        let self = this;

        // Create the map options
        let mapOptions = {
          zoom: 11,
          maxZoom: 17,
          center: new self.googleMaps.LatLng(52.774288, -3.158307),
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeId: "hybrid",
        };

        // Create the map
        self.map = new self.googleMaps.Map(self.mapElem, mapOptions);

        // Render overlays
        self.renderOverlay();

        // Load the first section
        self.goToSection(self.mapData[0], self.sectionsItems[0]);
    }

    /*-------------------------------------------------*\
        Render overlay
    \*-------------------------------------------------*/
    renderOverlay() {
        let self = this;

        // Set overlay bounds
        const overlayBounds = {
            north: 52.902541,
            south: 52.594802,
            east: -2.771831,
            west: -3.566077,
        };

        // Create overlay
        // KMZ import requires public facing URL. Upload KMZ to S3 bucket
        const overlay = new google.maps.KmlLayer(
            "https://camargue-ggen-vyrnwy-frankton.s3.eu-west-2.amazonaws.com/whole-scheme.kmz"
        );

        // Add overlay to map
        overlay.setMap(self.map);
    }

    /*-------------------------------------------------*\
        Render markers
    \*-------------------------------------------------*/
    renderMarkers(section) {
        let self = this;

        // Create empty array to store the markers
        let markers = [];

        // Get the markers from the section data
        section.activities.map(activity => {
            activity.markers.map(marker => {
                marker.number = activity.number;
                marker.sectionName = section.sectionName;
                marker.color = activity.color;
                markers.push(marker);
            });
        });

        // Create a new infoWindow
        let infoWindow = new self.googleMaps.InfoWindow();

        // Create the marker icon
        let icon = {
            path: 'M40.8,20.4A20.4,20.4,0,0,0,0,20.4C0,31.7,20.4,52.8,20.4,52.8S40.8,31.7,40.8,20.4',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1,
            scale: 1,
            labelOrigin: new google.maps.Point(20, 22),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20, 53)
        };

        // Create the bounds object, which will be configured later based on the marker locations
        let bounds = new self.googleMaps.LatLngBounds();

        // Loop through the markers
        for (let i = 0; i < markers.length; i++) {
            // Get the marker data
            let markerData = markers[i];

            // Create a new LatLng object to use for the marker position
            let latLng = new self.googleMaps.LatLng(markerData.lat, markerData.lng);

            icon.fillColor = markerData.color;

            // Create the marker object
            let marker = new self.googleMaps.Marker({
                position: latLng,
                map: self.map,
                icon: icon,
                label: {
                    color: '#fff', fontSize: '18px', fontWeight: '600',
                    text: `${markerData.number}`
                },
                title: markerData.description
            });

            // Add this marker to the self.setMarkers global array
            self.setMarkers.push(marker);

            // Handle marker clicks
            self.googleMaps.event.addListener(marker, "click", function (e) {

                // Build the content for the infoWindow
                let infoWindowContent = `
                    <div class="map__info-window">
                        <h2 class="map__info-window-heading">${markerData.sectionName}</h2>
                        ${markerData.description}
                        <p><a href="/en/viewpoint">View viewpoint</a></p>
                    </div>
                `;

                // Add the content to the infoWindow
                infoWindow.setContent(infoWindowContent);
                infoWindow.open(self.map, marker);
            });

            // Work out the area of the map containing markers
            bounds.extend(marker.position);

            // Create global function to center the map with all markers visible
            self.fitAndCenter = function () {
                self.map.setCenter(bounds.getCenter());
                self.map.fitBounds(bounds);
            };

            // Fit and center
            self.fitAndCenter();
        }
    }

    /*-------------------------------------------------*\
        Clear markers
    \*-------------------------------------------------*/
    clearMarkers() {
        let self = this;

        for (let i = 0; i < self.setMarkers.length; i++) {
            self.setMarkers[i].setMap(null);
        }

        self.setMarkers = [];
    }

    /*-------------------------------------------------*\
        Switch to the specified section
    \*-------------------------------------------------*/
    goToSection(section, sectionsItem) {
        let self = this;

        // Remove active class from all sections items
        self.sectionsItems.map(item => {
            item.classList.remove('is-active');
        });

        // Add active class to clicked button
        sectionsItem.classList.add('is-active');

        // Render the new set of markers
        self.renderMarkers(section);

        self.rendersectionBreakdownItems(section);

        self.selectedSectionsItemIndex = self.sectionsItems.findIndex((arrayItem) => arrayItem == sectionsItem);

        let currentZoom = self.map.getZoom();

        if (currentZoom > 13) {
            self.map.setZoom(13);
        }
    }

    /*-------------------------------------------------*\
        Render section breakdown items
    \*-------------------------------------------------*/
    rendersectionBreakdownItems(section) {
        let self = this;

        self.sectionBreakdownItems.innerHTML = '';

        // Add section breakdown items to the section breakdown dropdown
        section.activities.map(item => {
            self.sectionBreakdownItems.insertAdjacentHTML('beforeend', `
                <li class="section-breakdown__item">
                    <span class="section-breakdown__item-icon" style="color:${item.color}">
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 41 53'><path d='M40.8,20.4A20.4,20.4,0,0,0,0,20.4C0,31.7,20.4,52.8,20.4,52.8S40.8,31.7,40.8,20.4' style='fill:currentColor;fill-rule:evenodd'/></svg>
                        <span class="section-breakdown__item-icon-number">${item.number}</span>
                    </span>
                    <button class="[ section-breakdown__item-label ] [ stretched-link ]" type="button" data-section-breakdown-item-btn data-center-point-lat="${item.centerPoint.lat}" data-center-point-lng="${item.centerPoint.lng}">${item.summary}</button>
                </li>
            `);
        });

        // Get all the section breakdown item buttons
        let sectionBreakdownItemButtons = self.sectionBreakdown.querySelectorAll('[data-section-breakdown-item-btn]');

        // Loop through the buttons and listen for clicks
        [...sectionBreakdownItemButtons].map(item => {
            if (item.getAttribute('data-center-point-lat') && item.getAttribute('data-center-point-lng')) {
                item.addEventListener('click', evt => {
                    // Pan to the center point of the markers
                    self.map.panTo(new self.googleMaps.LatLng(
                        item.getAttribute('data-center-point-lat'),
                        item.getAttribute('data-center-point-lng')
                    ));

                    // Get the current zoom level
                    let currentZoom = self.map.getZoom();

                    if (currentZoom < 10) {
                        self.map.setZoom(10);
                    }
                });
            };
        });
    }

    /*-------------------------------------------------*\
        Events
    \*-------------------------------------------------*/
    events() {
        let self = this;

        // Loop through sections items
        self.sectionsItems.map(item => {

            // Handle sections item clicks
            item.addEventListener('click', evt => {
                evt.preventDefault();

                // Clear all markers from the map
                self.clearMarkers();

                // Get the handle of the section to switch to
                let sectionHandle = item.getAttribute('data-section');

                // Find the section from the mapData
                let newSection = self.mapData.find(section => section.sectionHandle == sectionHandle);

                // Load the section
                self.goToSection(newSection, item);
            });
        });

        // Re-center and fit the map when the window is resized
        self.googleMaps.event.addDomListener(window, 'resize', function () {
            self.fitAndCenter();
        });
    }

    /*-------------------------------------------------*\
        Get address form submission
    \*-------------------------------------------------*/
    getAddressFormSubmission(event) {
        let self = this;

        event.preventDefault();

        const address = self.addressFormInput.value;

        if (address.trim() !== '') {
            self.getAddress(address);
        } else {
            console.error('Please provide a postcode');
        }
    }

    /*-------------------------------------------------*\
        Get address
    \*-------------------------------------------------*/
    getAddress(address) {
        let self = this;

        const geocoder = new self.googleMaps.Geocoder();

        geocoder.geocode({'address': address}, function(results, status) {
            if (status === 'OK') {
                if (results[0]) {
                    let svg = '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 41 53" style="enable-background:new 0 0 41 53" xml:space="preserve"><path d="M40.8 20.4C40.8 9.1 31.7 0 20.4 0S0 9.1 0 20.4c0 11.3 20.4 32.4 20.4 32.4s20.4-21.1 20.4-32.4" style="fill-rule:evenodd;clip-rule:evenodd"/><path class="st1" fill="#fff" d="m20.5 13.5-8.6 7.1v7.2c0 .3.1.5.3.7.2.2.4.3.7.3h5.8V23h3.9v5.8h5.8c.3 0 .5-.1.7-.3.2-.2.3-.4.3-.7v-7.3l-8.9-7z"/><path class="st1" fill="#fff" d="m32.5 19-3.3-2.7v-6.1c0-.1 0-.3-.1-.3-.1-.1-.2-.1-.3-.1h-2.9c-.1 0-.3 0-.3.1-.1.1-.1.2-.1.3V13l-3.9-3c-.3-.3-.7-.4-1.1-.4-.4 0-.8.1-1.1.4L8.5 19c-.1.1-.2.2-.2.3 0 .1 0 .3.1.4l.9 1.1c.1.1.2.1.3.2.1 0 .2 0 .4-.1l10.4-8.7 10.4 8.7c.1.1.2.1.3.1.1 0 .2-.1.3-.2l.9-1.1c.1-.1.1-.2.1-.4.2-.1.2-.3.1-.3z"/></svg>';
                    let icon = {
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                        scaledSize: new google.maps.Size(60, 60)
                    };

                    let latLng = new self.googleMaps.LatLng(results[0].geometry.location.lat(), results[0].geometry.location.lng());

                    let marker = new self.googleMaps.Marker({
                        position: latLng,
                        map: self.map,
                        icon: icon,
                        title: 'address'
                    });

                    self.setMarkers.push(marker);
                } else {
                    console.error('No results found');
                }
            } else {
                console.error(status);
            }
        });
    }
}

export default Map;
